import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { FeatureFlags } from '../types/featureFlags'

/**
 * Same identity the rest of the booth API surface sends: the auth-store token
 * when the renderer has one, else the copy the main process holds. `authStore`
 * imports THIS module (it clears flags on logout), so the import is deferred to
 * call time to keep the cycle from biting at module-eval.
 */
const authHeaders = async (): Promise<Record<string, string>> => {
  try {
    const { useAuthStore } = await import('./authStore')
    const api = (window as unknown as { api?: { getAuthToken?: () => Promise<string | null> } }).api
    const token =
      useAuthStore.getState().user?.cookie ||
      (typeof api?.getAuthToken === 'function' ? await api.getAuthToken().catch(() => null) : null)
    return token ? { Authorization: `Bearer ${token}` } : {}
  } catch {
    // No preload (web build) or no token — the session cookie still applies.
    return {}
  }
}

/**
 * Feature availability flags for the photobooth app.
 *
 * Flags are resolved by the web backend (`GET /api/features`) for the logged-in
 * account and fetched once on startup. The map is persisted to localStorage so
 * the booth keeps working with the last-known flags while offline — a network
 * failure never blanks the UI, it just keeps the previous values.
 *
 * IDENTITY: send the same `Authorization: Bearer` token every other booth API
 * call uses, not just the session cookie. A booth whose next-auth cookie lapsed
 * used to get a 200 carrying the ANONYMOUS baseline, overwrite its real flag map
 * with it, and silently switch off every account-scoped feature — dual-camera,
 * camera-rest, reconnect-v2, auto-grant — while the booth otherwise kept running
 * on its licence. Bahela Kotak lost its dual-camera box-picker page this way on
 * 2026-08-12 with nothing in any log but the features quietly going dark.
 *
 * Belt and braces for the same failure: the backend now reports whether it could
 * identify the caller, and an UNAUTHENTICATED response never overwrites a map we
 * previously resolved while authenticated.
 *
 * Gate a feature in a component with `useFeatureFlag('<key>')` (hooks/).
 */
interface FeatureFlagsState {
  flags: FeatureFlags
  /** False until the first fetch (or offline hydrate) resolves. */
  loaded: boolean
  /** Epoch ms of the last successful fetch, or null. */
  lastFetch: number | null
  /**
   * True when the flags currently held were resolved for an IDENTIFIED account
   * (not the anonymous baseline). Persisted, so a cookie that lapses between
   * runs still can't quietly downgrade a booth that was authenticated before.
   */
  authenticated: boolean
  /**
   * Fetch flags from the backend. Pass `online = false` to skip the network and
   * keep whatever was persisted from a previous session (offline boot).
   */
  fetchFlags: (online?: boolean) => Promise<void>
  /** Read a single flag with a fallback for unknown keys (defaults to hidden). */
  getFlag: (key: string, fallback?: boolean) => boolean
  /** Reset to empty — called on logout / account switch. */
  clearFlags: () => void
}

export const useFeatureFlagsStore = create<FeatureFlagsState>()(
  persist(
    (set, get) => ({
      flags: {},
      loaded: false,
      lastFetch: null,
      authenticated: false,
      fetchFlags: async (online = true): Promise<void> => {
        // Offline: keep the persisted flags; just mark resolved so gates render.
        if (!online) {
          set({ loaded: true })
          return
        }
        try {
          const res = await fetch(`${process.env.VITE_API_URL}/api/features`, {
            credentials: 'include',
            headers: await authHeaders()
          })
          if (!res.ok) {
            // Unauthenticated / server error: keep last-known flags, don't wipe.
            set({ loaded: true })
            return
          }
          const data = await res.json()
          const flags =
            data && typeof data.flags === 'object' && data.flags ? (data.flags as FeatureFlags) : {}
          // `authenticated` is absent on older backends; treat that as "trusted"
          // so this client keeps working against a Studio that predates the field.
          const authenticated = data?.authenticated !== false
          if (!authenticated && get().authenticated) {
            // Anonymous baseline arriving after we had the real thing: the cookie
            // and the bearer both failed. Keep what we resolved while identified —
            // overwriting here is exactly what silently disabled paid features.
            console.warn(
              '[FEATURE-FLAGS] backend could not identify this booth; keeping the ' +
                'last authenticated flag map instead of the anonymous defaults'
            )
            set({ loaded: true })
            return
          }
          set({ flags, loaded: true, lastFetch: Date.now(), authenticated })
        } catch (error) {
          console.warn('[FEATURE-FLAGS] fetch failed, using cached/last-known flags:', error)
          set({ loaded: true })
        }
      },
      getFlag: (key: string, fallback = false): boolean => {
        const { flags } = get()
        return key in flags ? Boolean(flags[key]) : fallback
      },
      clearFlags: (): void => {
        set({ flags: {}, loaded: false, lastFetch: null, authenticated: false })
      }
    }),
    {
      name: 'feature-flags-store',
      partialize: (state) => ({
        flags: state.flags,
        lastFetch: state.lastFetch,
        authenticated: state.authenticated
      })
    }
  )
)
